/**
 * Pay now on the public invoice page (tracker item 38). Mobile-first.
 *
 * The card fields come from the processor through the payment form adapter
 * (this file never loads a processor script or sees a card number). The
 * amount defaults to the whole balance; partial payments down to the
 * tenant's minimum are allowed. "Save this card" and "Autopay future
 * invoices" (with the consent wording and version from the backend) are
 * optional. A PROCESSING answer means the outcome is unknown: the customer
 * is told not to pay again.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createAPIClient, IOnlinePaymentsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { usePaymentForm } from '../../features/online-payments/usePaymentForm';
import { PaymentFormError } from '../../features/online-payments/adapters/paymentFormAdapter';
import { amountError, cardLabel, formatUsd } from '../../features/online-payments/onlinePaymentsUi';
import { Toast } from '../../components/Toast';

interface Props {
  token: string;
  /** Called after a payment went through (the page reloads its totals) */
  onPaid?: () => void;
}

export function PublicPayPanel({ token, onPaid }: Props) {
  const api = useMemo<IOnlinePaymentsApi>(() => createAPIClient(), []);
  const [config, setConfig] = useState<types.PublicPayConfig | null>(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [autopay, setAutopay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<types.PublicPayResult | null>(null);
  const form = usePaymentForm(open && config?.available ? config.clientConfig : null);

  useEffect(() => {
    let active = true;
    // No pay config (or no online payments) simply means no Pay now
    Promise.resolve()
      .then(() => api.getPublicPayConfig(token))
      .then((c) => {
        if (!active) return;
        setConfig(c);
        setAmount(c.balanceDue);
      })
      .catch(() => { if (active) setConfig(null); });
    return () => { active = false; };
  }, [api, token]);

  if (!config) return null;
  if (!config.available) {
    return Number(config.balanceDue) > 0 && config.unavailableReason ? (
      <p className="print:hidden mt-6 text-sm text-gray-600">{config.unavailableReason}</p>
    ) : null;
  }

  if (result && result.status !== 'DECLINED') {
    const paid = result.status === 'PAID';
    return (
      <section aria-labelledby="pay-result-heading" className="print:hidden mt-8 rounded-lg border p-4 sm:p-5 space-y-2 border-gray-200 bg-gray-50">
        <h2 id="pay-result-heading" className={`text-lg font-semibold ${paid ? 'text-green-800' : 'text-amber-800'}`}>
          {paid ? `Payment of ${formatUsd(result.amount)} received` : 'Payment being confirmed'}
        </h2>
        <Toast kind={paid ? 'success' : 'info'}>{result.message}</Toast>
        <p className="text-sm text-gray-800">{result.message}</p>
        {paid && result.card && <p className="text-sm text-gray-700">Paid with {cardLabel(result.card)}.</p>}
        {paid && result.cardSaved && <p className="text-sm text-gray-700">Your card is saved{result.autopayEnabled ? ' and will pay future invoices automatically' : ''}.</p>}
        {paid && result.receiptUrl && (
          <a href={result.receiptUrl} target="_blank" rel="noopener noreferrer" className="inline-block text-sm font-semibold text-blue-700 hover:underline">View card receipt</a>
        )}
        {paid && Number(result.balanceDue) > 0 && <p className="text-sm text-gray-700">Remaining balance: {formatUsd(result.balanceDue)}.</p>}
      </section>
    );
  }

  const amountProblem = amountError(amount, config.minAmount, config.balanceDue);

  async function pay(event: React.FormEvent) {
    event.preventDefault();
    if (amountProblem || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { sourceToken } = await form.tokenize({ amount: Number(amount).toFixed(2), intent: 'CHARGE' });
      const answer = await api.payPublicInvoice(token, {
        sourceId: sourceToken,
        amount: Number(amount).toFixed(2),
        saveCard: saveCard || undefined,
        autopayConsent: saveCard && autopay ? true : undefined,
      });
      setResult(answer);
      if (answer.status === 'DECLINED') setError(answer.message);
      if (answer.status === 'PAID') onPaid?.();
    } catch (cause) {
      setError(cause instanceof PaymentFormError ? cause.message : getErrorMessage(cause, 'The payment could not be sent. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="pay-heading" className="print:hidden mt-8 border-t border-gray-200 pt-6">
      {!open ? (
        <button type="button" className={`${primaryButtonClass} w-full sm:w-auto text-base`} onClick={() => setOpen(true)}>
          Pay now
        </button>
      ) : (
        <form onSubmit={(e) => void pay(e)} className="space-y-4 max-w-md" aria-labelledby="pay-heading" noValidate>
          <h2 id="pay-heading" className="text-lg font-semibold">Pay invoice {config.invoiceNumber}</h2>
          <div>
            <label htmlFor="pay-amount" className={labelClass}>Amount (USD)</label>
            <input id="pay-amount" inputMode="decimal" className={`${inputClass} text-base`} value={amount} disabled={busy}
              onChange={(e) => setAmount(e.target.value)} aria-invalid={!!amountProblem} aria-describedby="pay-amount-hint" />
            <p id="pay-amount-hint" className={`mt-1 text-xs ${amountProblem ? 'text-red-700' : 'text-gray-500'}`}>
              {amountProblem ?? `Balance due ${formatUsd(config.balanceDue)}${Number(config.minAmount) < Number(config.balanceDue) ? `; at least ${formatUsd(config.minAmount)}` : ''}.`}
            </p>
          </div>
          <div>
            <span className={labelClass}>Card</span>
            <div ref={form.containerRef} data-testid="card-form" className="min-h-[3rem]" />
            {form.status === 'loading' && <p className="text-sm text-gray-500">Loading the secure card form…</p>}
            {form.error && <Toast kind="error">{form.error}</Toast>}
          </div>
          {config.cardsOnFile && (
            <div className="space-y-2 text-sm">
              <label className="flex items-start gap-3">
                <input type="checkbox" className="mt-0.5 h-5 w-5" checked={saveCard} disabled={busy}
                  onChange={(e) => { setSaveCard(e.target.checked); if (!e.target.checked) setAutopay(false); }} />
                <span>Save this card for future payments</span>
              </label>
              {config.autopayConsent && (
                <label className="flex items-start gap-3">
                  <input type="checkbox" className="mt-0.5 h-5 w-5" checked={autopay} disabled={busy || !saveCard}
                    onChange={(e) => setAutopay(e.target.checked)} aria-describedby="autopay-consent-text" />
                  <span>
                    <span className="font-medium">Pay future invoices automatically</span>
                    <span id="autopay-consent-text" className="block text-xs text-gray-600" data-consent-version={config.autopayConsent.version}>
                      {config.autopayConsent.text}
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}
          {error && <Toast kind="error">{error}</Toast>}
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => { setOpen(false); setError(null); setResult(null); }}>
              Cancel
            </button>
            <button type="submit" className={`${primaryButtonClass} text-base`} disabled={busy || !!amountProblem || form.status !== 'ready'}>
              {busy ? 'Paying…' : `Pay ${amountProblem ? '' : formatUsd(amount)}`.trim()}
            </button>
          </div>
          <p className="text-xs text-gray-500">Card details go directly to our card processor; we never see or store your card number.</p>
        </form>
      )}
    </section>
  );
}
